"use client";
import { useEffect, useState } from "react";
import { getCountryListAction } from "@/actions/user.actions";
import {
  CookiesEnum,
  cookiesValues,
  DEFAULT_COUNTRY,
  DEFAULT_COUNTRY_CODE,
  DEFAULT_COUNTRY_NAME,
} from "@/constants/constants";
import { CountryListItem } from "@/types/user.types";
import { getClientSideCookieValue } from "@/utils/common.helpers";

const DEFAULT_COUNTRY_ITEM: CountryListItem = {
  country: DEFAULT_COUNTRY,
  countryCode: DEFAULT_COUNTRY_CODE,
  countryName: DEFAULT_COUNTRY_NAME,
  countryFlag: "",
};

let cachedCountryList: CountryListItem[] | null = null;
let fetchPromise: Promise<CountryListItem[]> | null = null;

export function setInitialCountryList(data: CountryListItem[]) {
  if (!cachedCountryList && data.length > 0) {
    cachedCountryList = data;
  }
}

const getUserCountryCode = (): string => {
  return (
    getClientSideCookieValue(cookiesValues[CookiesEnum.UserCountry]) ??
    DEFAULT_COUNTRY
  );
};

const findCountryInList = (
  list: CountryListItem[],
  countryCode: string,
): CountryListItem | undefined => {
  return list.find((c) => c.country === countryCode);
};

const getInitialSelectedCountry = (): CountryListItem => {
  const userCountry = getUserCountryCode();
  if (cachedCountryList) {
    return (
      findCountryInList(cachedCountryList, userCountry) ??
      findCountryInList(cachedCountryList, DEFAULT_COUNTRY) ??
      DEFAULT_COUNTRY_ITEM
    );
  }
  if (userCountry === DEFAULT_COUNTRY) {
    return DEFAULT_COUNTRY_ITEM;
  }
  // No cached list and non-default country — return default for now, will update after fetch
  return DEFAULT_COUNTRY_ITEM;
};

export const useCountryList = () => {
  const [countries, setCountries] = useState<CountryListItem[]>(
    cachedCountryList ?? [DEFAULT_COUNTRY_ITEM],
  );
  const [selectedCountry, setSelectedCountry] = useState<CountryListItem>(
    getInitialSelectedCountry,
  );
  const [isLoading, setIsLoading] = useState(!cachedCountryList);

  useEffect(() => {
    const userCountry = getUserCountryCode();

    if (cachedCountryList) {
      setCountries(cachedCountryList);
      const match =
        findCountryInList(cachedCountryList, userCountry) ??
        findCountryInList(cachedCountryList, DEFAULT_COUNTRY);
      if (match) {
        setSelectedCountry(match);
      }
      setIsLoading(false);
      return;
    }

    if (!fetchPromise) {
      fetchPromise = getCountryListAction();
    }

    fetchPromise
      .then((data) => {
        if (data.length > 0) {
          cachedCountryList = data;
          setCountries(data);
          const match =
            findCountryInList(data, userCountry) ??
            findCountryInList(data, DEFAULT_COUNTRY);
          if (match) {
            setSelectedCountry(match);
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching country list", error);
      })
      .finally(() => {
        setIsLoading(false);
        fetchPromise = null;
      });
  }, []);

  return { countries, selectedCountry, setSelectedCountry, isLoading };
};
